/*
 * Free gift with purchase: auto-adds the Trucker Hat when the cart holds $300+ of
 * Tools & Supplies items, and removes it again if the cart drops below the threshold.
 *
 * Pricing the hat at $0 is handled by the existing automatic "Buy X get Y" discount;
 * this file only puts the hat in the cart. The threshold, collection/tag and gift product
 * live in sections/free-gift.liquid.
 */
(() => {
  const GIFT_PROPERTY = '_free_gift';
  const STORAGE_KEY = 'frsFreeGiftState'; // '' | 'added' | 'dismissed'

  let running = false;
  let rerunRequested = false;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const getState = () => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || '';
    } catch (e) {
      return '';
    }
  };

  const setState = (value) => {
    try {
      if (value) sessionStorage.setItem(STORAGE_KEY, value);
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* storage unavailable: worst case the gift can be re-added after a manual removal */
    }
  };

  const fetchCart = () =>
    fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());

  const fetchGiftData = () =>
    fetch(`${root()}?sections=free-gift&t=${Date.now()}`)
      .then((response) => response.json())
      .then((sections) => {
        const html = new DOMParser().parseFromString(sections['free-gift'] || '', 'text/html');
        const node = html.getElementById('FreeGiftData');
        return node ? JSON.parse(node.textContent) : null;
      });

  const isGiftLine = (item) => Boolean(item.properties && item.properties[GIFT_PROPERTY]);

  const addGift = (variantId) =>
    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { [GIFT_PROPERTY]: 'true' } }],
      }),
    }).then((response) => response.ok);

  const removeLine = (key) =>
    fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    }).then((response) => response.ok);

  const replaceInner = (target, html, selector) => {
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (target && source) target.innerHTML = source.innerHTML;
  };

  // Re-renders whichever cart UI is on the page after the gift line changed.
  const refreshCartUi = async () => {
    const cartItems = document.getElementById('main-cart-items');
    const cartFooter = document.getElementById('main-cart-footer');
    const drawer = document.querySelector('cart-drawer');

    if (cartItems && cartFooter) {
      const itemsId = cartItems.dataset.id;
      const footerId = cartFooter.dataset.id;
      const sections = await fetch(
        `${window.location.pathname}?sections=${[itemsId, footerId, 'cart-icon-bubble'].join(',')}&t=${Date.now()}`
      ).then((response) => response.json());
      if (!sections[itemsId] || !sections[footerId]) throw new Error('Cart sections missing');

      replaceInner(cartItems.querySelector('.js-contents') || cartItems, sections[itemsId], '.js-contents');
      replaceInner(cartFooter.querySelector('.js-contents') || cartFooter, sections[footerId], '.js-contents');
      replaceInner(document.getElementById('cart-icon-bubble'), sections['cart-icon-bubble'], '.shopify-section');
      return;
    }

    if (drawer) {
      const html = await fetch(`${window.routes.cart_url}?section_id=cart-drawer&t=${Date.now()}`).then((response) =>
        response.text()
      );
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      ['cart-drawer-items', '.cart-drawer__footer'].forEach((selector) => {
        const target = document.querySelector(selector);
        const source = parsed.querySelector(selector);
        if (target && source) target.replaceWith(source);
      });
    }

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) {
      const sections = await fetch(`${root()}?sections=cart-icon-bubble&t=${Date.now()}`).then((response) =>
        response.json()
      );
      replaceInner(bubble, sections['cart-icon-bubble'], '.shopify-section');
    }
  };

  const syncOnce = async () => {
    const [cart, data] = await Promise.all([fetchCart(), fetchGiftData()]);
    if (!data || !data.currencyOk) return;

    const giftLines = cart.items.filter(isGiftLine);
    const hasOwnGiftProduct = cart.items.some((item) => item.product_id === data.giftProductId && !isGiftLine(item));
    const eligible = data.qualifying >= data.threshold;
    const state = getState();
    let changed = false;

    if (!eligible) {
      setState('');
      for (const line of giftLines) {
        changed = (await removeLine(line.key)) || changed;
      }
    } else if (giftLines.length) {
      setState('added');
    } else if (hasOwnGiftProduct) {
      // Customer already added a Trucker Hat themselves; the discount applies to it.
    } else if (state === 'added' || state === 'dismissed') {
      // We added the gift earlier and the customer took it out; don't add it back.
      setState('dismissed');
    } else if (data.giftVariantId) {
      if (await addGift(data.giftVariantId)) {
        setState('added');
        changed = true;
      }
    }

    if (changed) {
      try {
        await refreshCartUi();
      } catch (e) {
        if (document.getElementById('main-cart-items')) window.location.reload();
      }
    }
  };

  const sync = async () => {
    if (running) {
      rerunRequested = true;
      return;
    }
    running = true;
    try {
      do {
        rerunRequested = false;
        await syncOnce();
      } while (rerunRequested);
    } catch (e) {
      console.error('[free-gift]', e);
    } finally {
      running = false;
    }
  };

  // Wait briefly after a cart event so the theme finishes painting its own response first.
  let syncTimer;
  const syncSoon = () => {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(sync, 600);
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      syncSoon();
    });
  }

  // The cart page is where a customer lands after add-to-cart when the theme uses a cart page.
  if (document.getElementById('main-cart-items')) sync();
})();
